import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError, ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import {
  subscriptionFeatures,
  type Subscription,
  type SubscriptionFeature,
} from '../../domain/inventory';
import { formatDiffValue } from '../item-detail/audit-diff';
import { humanizeIdentifier } from '../../lib/formatting';
import {
  buildSubscriptionPatch,
  SUBSCRIPTION_FIELD_LABELS,
  subscriptionEditErrorMessage,
  type SubscriptionEditDraft,
} from './subscription-draft';

const EDITABLE_FEATURES = subscriptionFeatures.filter(
  (feature): feature is Exclude<SubscriptionFeature, 'FEATURE_UNSPECIFIED'> =>
    feature !== 'FEATURE_UNSPECIFIED',
);

function toDateInputValue(value: string | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function fromDateInputValue(value: string): string | undefined {
  return value ? new Date(`${value}T00:00:00.000Z`).toISOString() : undefined;
}

function toRegionsInputValue(regions: string[] | undefined): string {
  return (regions ?? []).join(', ');
}

function fromRegionsInputValue(value: string): string[] {
  return value
    .split(',')
    .map((region) => region.trim())
    .filter(Boolean);
}

interface EditSubscriptionDrawerProps {
  customerId: string;
  subscription: Subscription;
  onClose: () => void;
}

/**
 * Edits both Subscription fields and SubscriptionType's feature list behind
 * one "Save," even though they're two backend resources reached by two RPCs
 * (see docs/decisions/ADR-0007-customer-entitlements-workspace.md). Only the
 * Subscription patch is version-checked; UpdateSubscriptionType has no
 * concurrency token exposed at all, so a conflict can only ever surface for
 * the term fields, not the feature list.
 */
export function EditSubscriptionDrawer({
  customerId,
  subscription,
  onClose,
}: EditSubscriptionDrawerProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SubscriptionEditDraft>({});
  const [featuresDraft, setFeaturesDraft] = useState<SubscriptionFeature[] | null>(null);
  const [loadedRevision, setLoadedRevision] = useState(subscription.revision);
  const [conflict, setConflict] = useState<{ latest: Subscription } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const [featuresAlreadySaved, setFeaturesAlreadySaved] = useState(false);

  const currentFeatures = featuresDraft ?? subscription.features;
  const current: Subscription & SubscriptionEditDraft = {
    ...subscription,
    revision: loadedRevision,
    ...draft,
  };
  const hasChanges = Object.keys(draft).length > 0 || featuresDraft !== null;

  const mutation = useMutation({
    mutationFn: async (revision: number) => {
      if (featuresDraft) {
        await inventoryApi.updateSubscriptionFeatures(customerId, featuresDraft);
      }
      if (Object.keys(draft).length > 0) {
        try {
          return await inventoryApi.updateCustomerSubscription(
            customerId,
            buildSubscriptionPatch(subscription.id, revision, draft),
          );
        } catch (error) {
          if (featuresDraft) setFeaturesAlreadySaved(true);
          throw error;
        }
      }
      return inventoryApi.getCustomerSubscription(customerId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customer-subscription', customerId] });
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ConflictError) {
        inventoryApi
          .getCustomerSubscription(customerId)
          .then((latest) => setConflict({ latest }))
          .catch(() =>
            setFormError(
              'Entitlements changed after they were loaded, and the latest version could not be fetched.',
            ),
          );
        return;
      }
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setFormError(subscriptionEditErrorMessage(error));
    },
  });

  function updateDraft<K extends keyof SubscriptionEditDraft>(
    key: K,
    value: SubscriptionEditDraft[K],
  ): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function toggleFeature(feature: SubscriptionFeature, enabled: boolean): void {
    const base = featuresDraft ?? subscription.features;
    setFeaturesDraft(enabled ? [...base, feature] : base.filter((f) => f !== feature));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    setUnknownOutcome(false);
    setFeaturesAlreadySaved(false);
    mutation.mutate(loadedRevision);
  }

  if (conflict) {
    const changedFields = Object.keys(draft) as (keyof SubscriptionEditDraft)[];
    return (
      <div className="drawer-overlay" role="presentation" onClick={onClose}>
        <div
          className="drawer conflict-drawer"
          role="dialog"
          aria-label="Version conflict"
          onClick={(event) => event.stopPropagation()}
        >
          <h2>Entitlements changed while you were editing</h2>
          <p className="conflict-summary">
            Loaded revision {loadedRevision} · current server revision {conflict.latest.revision}
          </p>
          {featuresAlreadySaved ? (
            <p role="alert" className="form-error">
              Feature access was saved. Review the remaining fields.
            </p>
          ) : null}
          {changedFields.length > 0 ? (
            <table className="conflict-table">
              <thead>
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Server</th>
                  <th scope="col">Your draft</th>
                </tr>
              </thead>
              <tbody>
                {changedFields.map((field) => (
                  <tr key={field}>
                    <td>{SUBSCRIPTION_FIELD_LABELS[field]}</td>
                    <td>{formatDiffValue(conflict.latest[field])}</td>
                    <td className="conflict-draft-cell">{formatDiffValue(draft[field])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div className="conflict-actions">
            <button type="button" onClick={onClose}>
              Discard my draft
            </button>
            <button
              type="button"
              onClick={() => {
                setLoadedRevision(conflict.latest.revision);
                setDraft({});
                setConflict(null);
              }}
            >
              Keep server values
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => {
                const nextRevision = conflict.latest.revision;
                setLoadedRevision(nextRevision);
                setConflict(null);
                mutation.mutate(nextRevision);
              }}
            >
              Reapply onto rev. {conflict.latest.revision}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer"
        aria-label="Edit entitlements"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <div>
            <span className="step-label">
              {subscription.customerId} · rev. {loadedRevision}
            </span>
            <h2>Edit entitlements</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="drawer-version-note">Changed fields only. Conflicts require review.</p>

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({
                queryKey: ['customer-subscription', customerId],
              });
              onClose();
            }}
          />
        ) : null}

        <label style={{ marginBottom: '-2px' }}>Feature access</label>
        <div className="checkbox-grid">
          {EDITABLE_FEATURES.map((feature) => (
            <label className="checkbox-label" key={feature}>
              <input
                type="checkbox"
                checked={currentFeatures.includes(feature)}
                onChange={(event) => toggleFeature(feature, event.target.checked)}
              />
              {humanizeIdentifier(feature)}
            </label>
          ))}
        </div>

        <div className="form-row">
          <label>
            Entitled systems
            <input
              type="number"
              min={0}
              value={current.numberOfSystems ?? ''}
              onChange={(event) =>
                updateDraft(
                  'numberOfSystems',
                  event.target.value === '' ? undefined : Number(event.target.value),
                )
              }
            />
          </label>
          <label>
            End date
            <input
              type="date"
              value={toDateInputValue(current.endTime)}
              onChange={(event) => updateDraft('endTime', fromDateInputValue(event.target.value))}
            />
          </label>
        </div>
        <label>
          Contract name
          <input
            type="text"
            value={current.contractName ?? ''}
            onChange={(event) => updateDraft('contractName', event.target.value)}
          />
        </label>
        <label>
          Contract URL
          <input
            type="url"
            value={current.contractUrl ?? ''}
            onChange={(event) => updateDraft('contractUrl', event.target.value)}
          />
        </label>
        <label>
          Regions
          <input
            type="text"
            value={toRegionsInputValue(current.regions)}
            onChange={(event) => updateDraft('regions', fromRegionsInputValue(event.target.value))}
            placeholder="Comma-separated"
          />
        </label>
        <label>
          Notes
          <textarea
            value={current.notes ?? ''}
            onChange={(event) => updateDraft('notes', event.target.value)}
            rows={3}
          />
        </label>

        <div className="drawer-actions">
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={mutation.isPending || !hasChanges}>
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
